import { useState } from 'react';
import styled from 'styled-components';
import { LuExpand } from 'react-icons/lu';

import SidebarTemplate from '../sidebarElements/SidebarTemplate';
import HexagonAccordion from '../sidebarElements/HexagonAccordion';

import { formatNumber, safePercent } from '@utils/helpers';
import { formatArea, formatLength } from '@utils/measurementSystem';
import { hasStemEstimates, getStemEstimateRows, computePerHaOptions } from '@utils/stemEstimates';
import { useProject } from '@contexts/Project.context';
import { Button } from 'react-bootstrap';

const Unit = styled.span`
	text-wrap: nowrap;
`;

const Menu = styled.div`
	color: ${({ theme }) => theme.colors.textColor};
	padding-top: 10px;
	padding-bottom: 20px;
`;

const sidebarId = 'hexagonSummary';

const HexagonSummarySidebar = () => {
	const { hexagonInfo } = useProject();

	const hexagonsSummary = hexagonInfo?.metadata?.total;

	if (!hexagonsSummary?.detections) return;

	return (
		<SidebarTemplate
			zindex={2}
			sidebarId={sidebarId}
			header="Hexagons total summary"
			closeable={true}>
			<Menu>
				<HexagonAccordion hexagonData={hexagonsSummary} />
			</Menu>
		</SidebarTemplate>
	);
};
export default HexagonSummarySidebar;

/* ─── Styled Components for Preview ─── */

const SummaryWrapper = styled.div`
	margin: 5px 0 10px;
	font-size: 0.9em;
`;

const TopStats = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 8px;
	margin-bottom: 12px;
`;

const StatCard = styled.div`
	border-radius: 4px;
	padding: 8px 0;

	span:first-child {
		display: block;
		font-size: 0.8em;
		color: ${({ theme }) => theme.colors.textAccent};
		margin-bottom: 2px;
	}

	span:last-child {
		font-weight: 600;
		font-size: 1em;
		white-space: nowrap;
	}
`;

const StemEstimatesSection = styled.div`
	margin-bottom: 12px;
`;

const SectionLabel = styled.div`
	font-size: 0.8em;
	font-weight: 600;
	color: ${({ theme }) => theme.colors.textAccent};
	text-transform: uppercase;
	letter-spacing: 0.03em;
	margin-bottom: 6px;
	display: flex;
	align-items: baseline;
	gap: 8px;
`;

const StemRow = styled.div`
	display: grid;
	grid-template-columns: auto 1fr;
	gap: 8px;
	align-items: baseline;
	margin-bottom: 4px;

	span:first-child {
		color: ${({ theme }) => theme.colors.textAccent};
		font-size: 0.85em;
	}

	span:last-child {
		text-align: right;
		font-size: 0.82em;
	}
`;

const CategoryDivider = styled.hr`
	border: none;
	border-top: 1px solid ${({ theme }) => theme.colors.border};
	margin: 12px 0;
`;

const ComparisonTableWrapper = styled.div`
	overflow-x: auto;
	margin: 0 -2px;
	padding: 0 2px;
`;

const ComparisonTable = styled.table`
	width: 100%;
	border-collapse: collapse;
	font-size: 0.82em;

	th,
	td {
		padding: 5px 6px;
		text-align: right;
		white-space: nowrap;
	}

	th:first-child,
	td:first-child {
		text-align: left;
		color: ${({ theme }) => theme.colors.textAccent};
		position: sticky;
		left: 0;
		background: ${({ theme }) => theme.colors.background};
	}

	th {
		font-weight: 600;
		text-transform: capitalize;
		border-bottom: 1px solid ${({ theme }) => theme.colors.border};
		padding-bottom: 6px;
	}

	tr:not(:last-child) td {
		border-bottom: 1px solid ${({ theme }) => theme.colors.border}20;
	}
`;

const ToggleButton = styled.button`
	background: none;
	border: none;
	color: ${({ theme }) => theme.colors.textAccent};
	font-size: 0.8em;
	cursor: pointer;
	padding: 4px 0;
	text-decoration: underline;
	text-underline-offset: 2px;

	&:hover {
		color: ${({ theme }) => theme.colors.textColor};
	}
`;

const MoreButtonWrapper = styled.div`
	margin-top: 14px;
	display: flex;
	justify-content: flex-end;
	align-items: center;
`;

/* ─── Preview Component ─── */

export const HexagonSummaryPreview = () => {
	const { setActiveSidebar, hexagonInfo } = useProject();
	const [showBreakdown, setShowBreakdown] = useState(false);

	if (!hexagonInfo) return null;

	const hexagonsSummary = hexagonInfo?.metadata?.total;
	const {
		metadata: {
			count,
			area,
			stems_low,
			stems_medium,
			stems_high,
			stems_per_ha_low,
			stems_per_ha_medium,
			stems_per_ha_high,
		} = {},
		detections = {},
	} = hexagonsSummary || {};

	const [areaNumber, areaUnit] = formatArea(area);
	const showStemEstimates = hasStemEstimates(stems_low, stems_medium, stems_high);
	const detectionKeys = Object.keys(detections || {});
	const hasDetections = detectionKeys.length > 0;

	const showMore = () => {
		setActiveSidebar({ sidebarId, parentId: 'layerView' });
	};

	return (
		<SummaryWrapper>
			{/* Top-level stats in a compact card grid */}
			<TopStats>
				{count != null && (
					<StatCard>
						<span>Total Objects</span>
						<span>{formatNumber(count)}</span>
					</StatCard>
				)}
				{area != null && (
					<StatCard>
						<span>Total Area</span>
						<span>
							{areaNumber} {areaUnit}
						</span>
					</StatCard>
				)}
			</TopStats>

			{/* Stem estimates - compact rows */}
			{showStemEstimates && (
				<StemEstimatesSection>
					<SectionLabel>Stem Estimates</SectionLabel>
					{getStemEstimateRows(stems_low, stems_medium, stems_high, {
						perHaLow: stems_per_ha_low,
						perHaMedium: stems_per_ha_medium,
						perHaHigh: stems_per_ha_high,
					}).map(row => (
						<StemRow key={row.key}>
							<span>{row.label.replace('Est. Stems ', '').replace(/[()]/g, '')}</span>
							<span>{row.value}</span>
						</StemRow>
					))}
				</StemEstimatesSection>
			)}

			{/* Side-by-side comparison table for detection categories */}
			{hasDetections && (
				<>
					<CategoryDivider />
					<SectionLabel>
						Breakdown by Classes
						{detectionKeys.length > 1 && (
							<ToggleButton onClick={() => setShowBreakdown(prev => !prev)}>
								{showBreakdown ? 'Hide' : 'Show'}
							</ToggleButton>
						)}
					</SectionLabel>

					{(showBreakdown || detectionKeys.length === 1) && (
						<ComparisonTableWrapper>
							<ComparisonTable>
								<thead>
									<tr>
										<th></th>
										{detectionKeys.map(key => (
											<th key={key}>{key}</th>
										))}
									</tr>
								</thead>
								<tbody>
									<ComparisonRow
										label="Count"
										detectionKeys={detectionKeys}
										detections={detections}
										renderValue={det => {
											const pct = safePercent(det.count, count);
											return (
												<>
													{formatNumber(det.count)}{' '}
													<small>({pct}%)</small>
												</>
											);
										}}
									/>
									<ComparisonRow
										label="Area"
										detectionKeys={detectionKeys}
										detections={detections}
										renderValue={det => {
											if (!det.sum_object_area || det.sum_object_area === 0)
												return '-';
											const [num, unit] = formatArea(det.sum_object_area);
											return (
												<>
													{num} <Unit>{unit}</Unit>
												</>
											);
										}}
									/>
									<ComparisonRow
										label="Mean Height"
										detectionKeys={detectionKeys}
										detections={detections}
										renderValue={det => {
											if (!det.mean_height || det.mean_height === 0)
												return '-';
											const [num, unit] = formatLength(det.mean_height);
											return (
												<>
													{num} {unit}
												</>
											);
										}}
									/>
									<ComparisonRow
										label="Stems (Low)"
										detectionKeys={detectionKeys}
										detections={detections}
										renderValue={(det, key, totalArea) => {
											const stems = det.estimated_stems;
											if (stems?.low == null) return '-';
											const perHa =
												totalArea > 0
													? computePerHaOptions(stems, totalArea)
													: {};
											return formatStemCell(stems.low, perHa.perHaLow);
										}}
										totalArea={area}
									/>
									<ComparisonRow
										label="Stems (Med)"
										detectionKeys={detectionKeys}
										detections={detections}
										renderValue={(det, key, totalArea) => {
											const stems = det.estimated_stems;
											if (stems?.medium == null) return '-';
											const perHa =
												totalArea > 0
													? computePerHaOptions(stems, totalArea)
													: {};
											return formatStemCell(stems.medium, perHa.perHaMedium);
										}}
										totalArea={area}
									/>
									<ComparisonRow
										label="Stems (High)"
										detectionKeys={detectionKeys}
										detections={detections}
										renderValue={(det, key, totalArea) => {
											const stems = det.estimated_stems;
											if (stems?.high == null) return '-';
											const perHa =
												totalArea > 0
													? computePerHaOptions(stems, totalArea)
													: {};
											return formatStemCell(stems.high, perHa.perHaHigh);
										}}
										totalArea={area}
									/>
								</tbody>
							</ComparisonTable>
						</ComparisonTableWrapper>
					)}
				</>
			)}

			<MoreButtonWrapper>
				<Button
					variant="dark"
					size="sm"
					className="py-1 px-2 d-flex align-items-center justify-content-center gap-2 w-100"
					onClick={showMore}>
					<LuExpand style={{ verticalAlign: 'middle' }} />
					<span>More Details</span>
				</Button>
			</MoreButtonWrapper>
		</SummaryWrapper>
	);
};

/* ─── Helper Components ─── */

const ComparisonRow = ({ label, detectionKeys, detections, renderValue, totalArea }) => (
	<tr>
		<td>{label}</td>
		{detectionKeys.map(key => (
			<td key={key}>{renderValue(detections[key], key, totalArea)}</td>
		))}
	</tr>
);

const formatStemCell = (count, perHa) => {
	if (count == null) return '-';
	const formatted = formatNumber(count, 0);
	if (perHa == null) return formatted;
	return `${formatted} / ${formatNumber(perHa, 0)} pr ha`;
};
